import styled, { keyframes, createGlobalStyle } from 'styled-components';

// Keyframes para animações
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const expandWidth = keyframes`
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
`;

export const GlobalStyle = createGlobalStyle`
  :root {
    --color-primary-a: #009bd9;
    --color-primary-b: #024995;
    --color-primary-c: #f39200;
    --color-support: #a4acb1;
    --color-primary-a-rgb: 0, 155, 217;
    --color-primary-b-rgb: 2, 73, 149;
    --color-primary-c-rgb: 243, 146, 0;
    --color-support-rgb: 164, 172, 177;
  }
`;

// Container principal
export const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

// Header e navegação
export const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #024995 0%, #009bd9 60%, #009bd9 100%);
  color: white;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

export const Logo = styled.div`
  a {
    display: flex;
    align-items: center;
    align-self: center;
    text-decoration: none;
    background: white;
    border-radius: 10px;
    padding: 5px 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transition: opacity 0.2s ease;
    
    img {
      height: 36px;
      width: auto;
      display: block;
      object-fit: contain;

      @media (max-width: 768px) {
        height: 30px;
      }

      @media (max-width: 480px) {
        height: 26px;
      }
    }

    &:hover {
      opacity: 0.85;
    }
  }
`;

export const Nav = styled.nav`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  width: 100%;
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr auto;
    gap: 1rem;
  }
`;

export const NavLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  justify-self: center;
  
  a {
    text-decoration: none;
    color: white;
    font-weight: 500;
    transition: color 0.3s ease;
    white-space: nowrap;
    font-size: 0.95rem;
    
    &:hover {
      color: var(--color-primary-c);
    }
  }

  @media (max-width: 1500px) {
    gap: 1rem;

    a {
      font-size: 0.9rem;
    }
  }

  @media (max-width: 1440px) {
    gap: 0.75rem;

    a {
      font-size: 0.85rem;
    }
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

export const NavLink = styled(({ isActive, ...props }) => <a {...props} />)`
  color: white;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  position: relative;
  padding: 0.5rem 0;
  transition: all 0.3s ease;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: white;
    transition: width 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-2px);
    
    &::after {
      width: 100%;
    }
  }
  
  ${({ isActive }) => isActive && `
    &::after {
      width: 100%;
    }
  `}
`;

export const LoginButton = styled.button`
  background: white;
  color: var(--color-primary-b);
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  justify-self: end;
  
  &:hover {
    background: #f8f9fa;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(var(--color-primary-a-rgb), 0.2);
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

export const MobileLoginButton = styled.button`
  background: white;
  color: var(--color-primary-b);
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 0.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f8f9fa;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(var(--color-primary-a-rgb), 0.2);
  }
`;

// Menu mobile
export const MobileMenuContainer = styled.div`
  display: none;
  position: relative;
  justify-self: end;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

interface HamburgerButtonProps {
  open: boolean;
}

export const HamburgerButton = styled.button<HamburgerButtonProps>`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  width: 2rem;
  height: 2rem;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 10;
  
  span {
    width: 2rem;
    height: 0.25rem;
    background: white;
    border-radius: 10px;
    transition: all 0.3s linear;
    position: relative;
    transform-origin: 1px;
    
    &:first-child {
      transform: ${({ open }) => (open ? 'rotate(45deg)' : 'rotate(0)')};
    }
    
    &:nth-child(2) {
      opacity: ${({ open }) => (open ? '0' : '1')};
      transform: ${({ open }) => (open ? 'translateX(20px)' : 'translateX(0)')};
    }
    
    &:nth-child(3) {
      transform: ${({ open }) => (open ? 'rotate(-45deg)' : 'rotate(0)')};
    }
  }
`;

interface MobileMenuProps {
  isOpen: boolean;
}

export const MobileMenu = styled.div<MobileMenuProps>`
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  flex-direction: column;
  background: linear-gradient(160deg, #024995 0%, #009bd9 100%);
  position: absolute;
  top: 60px;
  right: 0;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255,255,255,0.08);
  width: 220px;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  animation: ${scaleIn} 0.3s ease-out forwards;
  z-index: 5;
`;

export const MobileMenuLink = styled.button`
  text-decoration: none;
  color: rgba(255, 255, 255, 0.88);
  font-weight: 500;
  font-size: 0.95rem;
  margin: 0;
  transition: all 0.2s ease;
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  text-align: left;
  cursor: pointer;
  padding: 0.65rem 0.5rem;
  width: 100%;
  
  &:hover {
    color: white;
    padding-left: 0.75rem;
  }
`;

// Hero Section do Zoom
export const HeroSection = styled.section`
  background: linear-gradient(135deg, var(--color-primary-b) 0%, var(--color-primary-a) 60%, var(--color-primary-a) 100%);
  padding: 5rem 2rem 4rem;
  position: relative;
  overflow: hidden;
  text-align: center;
  color: white;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -20%;
    width: 60%;
    height: 200%;
    background: radial-gradient(ellipse, rgba(var(--color-primary-c-rgb), 0.15) 0%, transparent 70%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -50%;
    right: -20%;
    width: 60%;
    height: 200%;
    background: radial-gradient(ellipse, rgba(255, 255, 255, 0.08) 0%, transparent 70%);
    pointer-events: none;
  }
`;

export const HeroContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

export const HeroBadge = styled.span`
  display: inline-block;
  background: linear-gradient(135deg, var(--color-primary-c) 0%, rgba(var(--color-primary-c-rgb), 0.9) 100%);
  color: #1a202c;
  padding: 0.5rem 1.5rem;
  border-radius: 50px;
  font-size: 0.85rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 1.5rem;
  animation: ${scaleIn} 0.6s ease-out;
  box-shadow: 0 4px 15px rgba(var(--color-primary-c-rgb), 0.4);
`;

export const HeroTitle = styled.h1`
  font-size: 3.2rem;
  font-weight: 900;
  line-height: 1.15;
  margin-bottom: 1rem;
  animation: ${fadeInUp} 0.7s ease-out;

  span {
    display: block;
    background: linear-gradient(90deg, var(--color-primary-c), rgba(var(--color-primary-c-rgb), 0.7));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

export const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 650px;
  margin: 0 auto 3rem;
  line-height: 1.7;
  animation: ${fadeInUp} 0.8s ease-out;

  @media (max-width: 768px) {
    font-size: 1.05rem;
  }
`;

export const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 2rem;
  text-align: left;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.8rem;
  }
`;

export const BenefitItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 0.9rem 1.2rem;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    transform: translateY(-2px);
  }

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }

  p {
    font-size: 0.92rem;
    color: rgba(255, 255, 255, 0.95);
    line-height: 1.45;
    margin: 0;
  }
`;

export const ResultBox = styled.div`
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 1.5rem 2rem;
  border-radius: 16px;
  font-size: 1.05rem;
  color: white;
  line-height: 1.6;
  max-width: 700px;
  margin: 0 auto;
  animation: ${fadeInUp} 1s ease-out;

  strong {
    color: #FFC119;
  }
`;

export const ScrollCTA = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, var(--color-primary-c) 0%, rgba(var(--color-primary-c-rgb), 0.9) 100%);
  color: #1a202c;
  border: none;
  padding: 1rem 2.5rem;
  border-radius: 50px;
  font-weight: 800;
  font-size: 1.1rem;
  cursor: pointer;
  margin-top: 2rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 8px 30px rgba(var(--color-primary-c-rgb), 0.4);
  transition: all 0.3s ease;
  animation: ${fadeInUp} 1.1s ease-out;

  &:hover {
    transform: translateY(-3px) scale(1.03);
    box-shadow: 0 12px 40px rgba(var(--color-primary-c-rgb), 0.5);
  }

  svg {
    animation: bounce 1.5s infinite;
  }

  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(4px); }
  }
`;

// Seção de Depoimentos
export const TestimonialsSection = styled.section`
  padding: 5rem 2rem;
  background: linear-gradient(135deg, var(--color-primary-b) 0%, var(--color-primary-a) 100%);
  text-align: center;
  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 20%, rgba(255,255,255,0.08) 0%, transparent 50%),
          radial-gradient(circle at 70% 80%, rgba(var(--color-primary-c-rgb), 0.08) 0%, transparent 50%);
    pointer-events: none;
  }
`;

export const TestimonialsTitle = styled.h2`
  font-size: 2.2rem;
  font-weight: 800;
  color: white;
  margin-bottom: 0.5rem;
  position: relative;
  z-index: 1;

  span {
    background: linear-gradient(90deg, var(--color-primary-c), rgba(var(--color-primary-c-rgb), 0.7));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

export const TestimonialsSubtitle = styled.p`
  font-size: 1.05rem;
  color: rgba(255,255,255,0.75);
  margin-bottom: 3rem;
  position: relative;
  z-index: 1;
`;

export const CarouselContainer = styled.div`
  position: relative;
  max-width: 900px;
  margin: 0 auto;
  z-index: 1;
`;

export const CarouselViewport = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  padding: 1rem 0;

  @media (max-width: 768px) {
    gap: 0;
  }
`;

interface SlideProps {
  isActive?: boolean;
  position?: string;
}

export const CarouselSlide = styled.div<SlideProps>`
  flex-shrink: 0;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;

  ${({ isActive, position }) => isActive ? `
    width: 380px;
    height: 480px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.35);
    transform: scale(1);
    z-index: 2;
    opacity: 1;
  ` : `
    width: 260px;
    height: 480px;
    box-shadow: 0 8px 30px rgba(0,0,0,0.2);
    transform: scale(0.88);
    z-index: 1;
    opacity: 0.5;
    filter: blur(1.5px);
  `}

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    ${({ isActive }) => isActive ? `
      width: 85vw;
      height: auto;
    ` : `
      display: none;
    `}

    img {
      height: auto;
      object-fit: contain;
    }
  }
`;

export const CarouselControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  margin-top: 2rem;
`;

export const CarouselButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.3);
  background: rgba(255,255,255,0.1);
  backdrop-filter: blur(10px);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255,255,255,0.25);
    border-color: rgba(255,255,255,0.5);
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }
`;

export const CarouselDots = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

export const CarouselDot = styled.button<{ active: boolean }>`
  width: ${({ active }) => active ? '24px' : '8px'};
  height: 8px;
  border-radius: 4px;
  border: none;
  background: ${({ active }) => active ? 'var(--color-primary-c)' : 'rgba(255,255,255,0.35)'};
  cursor: pointer;
  transition: all 0.4s ease;

  &:hover {
    background: ${({ active }) => active ? 'rgba(var(--color-primary-c-rgb), 0.75)' : 'rgba(255,255,255,0.55)'};
  }
`;

export const CarouselCounter = styled.span`
  color: rgba(255,255,255,0.6);
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 1px;
  min-width: 50px;
`;

// Seção PIX
export const PixBox = styled.div`
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border: 2px solid #4caf50;
  border-radius: 16px;
  padding: 1.8rem 2rem;
  margin-top: 2rem;
  text-align: center;

  h3 {
    font-size: 1.1rem;
    font-weight: 800;
    color: #2e7d32;
    margin: 0 0 0.3rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }

  p {
    font-size: 0.92rem;
    color: #4a5568;
    margin: 0.3rem 0;
    line-height: 1.5;
  }

  .pix-discount {
    display: inline-block;
    background: #4caf50;
    color: white;
    padding: 0.2rem 0.8rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 700;
    margin: 0.5rem 0;
  }

  .pix-price {
    font-size: 1.8rem;
    font-weight: 900;
    color: #2e7d32;
    margin: 0.3rem 0;
  }
`;

export const WhatsAppButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  color: white;
  padding: 0.8rem 2rem;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  margin-top: 0.8rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(37, 211, 102, 0.3);

  &:hover, &:visited, &:active {
    color: white;
    text-decoration: none;
  }

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 6px 20px rgba(37, 211, 102, 0.4);
  }
`;

// Seção do Formulário
export const FormSection_Wrapper = styled.section`
  padding: 4rem 2rem;
  background: linear-gradient(180deg, #f0f4f8 0%, #ffffff 100%);
  position: relative;
`;

export const FormContentWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

export const FormCard = styled.form`
  background: white;
  border-radius: 24px;
  padding: 3rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

export const FormTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 800;
  color: #1a202c;
  text-align: center;
  margin-bottom: 2rem;
`;

export const FormSection = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e2e8f0;

  &:last-of-type {
    border-bottom: none;
    margin-bottom: 1.5rem;
  }
`;

export const FormSectionTitle = styled.h3`
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--color-primary-a);
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
`;

export const FormLabel = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 0.4rem;
`;

export const FormInput = styled.input`
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  background: #f7fafc;

  &:focus {
    outline: none;
    border-color: var(--color-primary-a);
    background: white;
    box-shadow: 0 0 0 3px rgba(var(--color-primary-a-rgb), 0.1);
  }

  &:disabled {
    background: #edf2f7;
    color: #a0aec0;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #a0aec0;
  }
`;

export const FormSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  background: #f7fafc;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--color-primary-a);
    background: white;
    box-shadow: 0 0 0 3px rgba(var(--color-primary-a-rgb), 0.1);
  }
`;

export const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: var(--color-primary-a);
    cursor: pointer;
  }

  label {
    font-size: 0.9rem;
    color: #4a5568;
    cursor: pointer;
  }
`;

export const SubmitButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, var(--color-primary-a) 0%, var(--color-primary-b) 100%);
  color: white;
  border: none;
  padding: 1.2rem;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 8px 25px rgba(var(--color-primary-a-rgb), 0.3);

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, var(--color-primary-b) 0%, var(--color-primary-a) 100%);
    transform: translateY(-2px);
    box-shadow: 0 12px 35px rgba(var(--color-primary-a-rgb), 0.4);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

interface StatusMessageProps {
  type: string;
}

export const StatusMessage = styled.div<StatusMessageProps>`
  padding: 1rem 1.5rem;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 1rem;

  ${({ type }) => type === 'success' ? `
    background: #e8f5e9;
    color: #2e7d32;
    border: 1px solid #a5d6a7;
  ` : `
    background: #ffebee;
    color: #c62828;
    border: 1px solid #ef9a9a;
  `}
`;

export const FieldError = styled.span`
  color: #c62828;
  font-size: 0.8rem;
  font-weight: 500;
  margin-top: 0.3rem;
  display: block;
`;

// Footer
export const Footer = styled.footer`
  background: linear-gradient(135deg, var(--color-primary-b) 0%, var(--color-primary-a) 100%);
  color: white;
  padding: 4rem 2rem 1rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 80%, rgba(var(--color-primary-a-rgb), 0.08) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(var(--color-primary-c-rgb), 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
`;

export const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 3rem;
  position: relative;
  z-index: 1;
`;

export const FooterSection = styled.div`
  h4 {
    font-size: 1.3rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    background: linear-gradient(135deg, var(--color-primary-c) 0%, rgba(var(--color-primary-c-rgb), 0.9) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  p, a {
    color: #cbd5e0;
    text-decoration: none;
    line-height: 1.7;
    display: block;
    margin-bottom: 0.8rem;
    font-weight: 400;
    transition: all 0.3s ease;
    
    &:hover {
      color: var(--color-primary-c);
      transform: translateX(4px);
    }
  }
`;

export const FooterLogo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  
  img {
    height: 58px;
    width: auto;
    object-fit: contain;
    opacity: 0.92;
    transition: opacity 0.2s ease;
    
    &:hover {
      opacity: 1;
    }
  }
`;

export const FooterBottom = styled.div`
  max-width: 1200px;
  margin: 3rem auto 0;
  padding-top: 2.5rem;
  border-top: 1px solid rgba(var(--color-primary-c-rgb), 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #cbd5e0;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

export const SocialIcons = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin-top: 0rem;
  
  @media (max-width: 768px) {
    gap: 1rem;
    margin-top: 0.8rem;
  }
`;

export const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: #cbd5e0;
  text-decoration: none;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  svg {
    width: 24px;
    height: 24px;
    transition: all 0.3s ease;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: var(--color-primary-c);
    transform: translateY(-3px) scale(1.1);
    box-shadow: 0 8px 25px rgba(var(--color-primary-c-rgb), 0.3);
    
    svg {
      transform: scale(1.1);
    }
  }
  
  &:active {
    transform: translateY(-1px) scale(1.05);
  }
  
  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;